import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { LoginModel } from '../core/models/auth/login.model';
import { RegisterModel } from '../core/models/auth/register.model';
import { AuthHttpService } from './state/auth-http.service';
import * as fromAuthActions from './state/auth.actions';

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss'],
  standalone: false,
})
export class AuthComponent implements OnInit, OnDestroy {
  public isLoginMode = signal(true);
  public errorMsg = null;
  public requestSent = false;

  private readonly subs = new SubSink();
  private readonly authHttpService = inject(AuthHttpService);
  private readonly actions$ = inject(Actions);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  ngOnInit(): void {
    this.requestSent = true;
    this.subs.sink = this.authHttpService.authenticate().subscribe();

    this.initEvents();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public onSubmit(authModel: LoginModel | RegisterModel): void {
    this.requestSent = true;
    let authObs$: Observable<LoginModel | RegisterModel>;

    if (authModel instanceof LoginModel) {
      authObs$ = this.authHttpService.login(authModel);
    } else {
      authObs$ = this.authHttpService.register(authModel);
    }

    this.subs.sink = authObs$
      .pipe(switchMap(() => this.authHttpService.authenticate()))
      .subscribe();
  }

  private initEvents(): void {
    this.subs.sink = this.actions$.pipe(ofType(fromAuthActions.setLoggedInUser)).subscribe(() => {
      const p = this.route.snapshot.queryParamMap.get('p');
      let redirectTarget: string | UrlTree = '/';

      // harden against open-redirects and loops
      if (p && p.startsWith('/') && !p.startsWith('//') && !p.startsWith('/auth')) {
        try {
          redirectTarget = this.router.parseUrl(p);
        } catch {
          redirectTarget = '/';
        }
      } else {
        redirectTarget = '/';
      }

      // Use replaceUrl so the /auth page isn’t left in history.
      void this.router.navigateByUrl(redirectTarget, { replaceUrl: true });
    });

    this.subs.sink = this.actions$.pipe(ofType(fromAuthActions.unauthorized)).subscribe(() => {
      this.requestSent = false;
    });
  }
}
